import React, { useCallback, useState, useEffect } from 'react';

import { formatAccountNumber } from '@eika-infrastruktur/format';
import { Button, LinkButton } from '@eika/button';
import { CheckboxPanels } from '@eika/checkbox';
import { useViewType, ViewType } from '@eika/hooks';

import type { Account } from '../../../generated-types';
import useTranslation from '../../../hooks/useTranslation';
import { EikaModal } from '../../EikaModal';
import { StickyFooterRow } from '../../shared/StickyFooter';

import './FuturePaymentsFilterModal.scss';

type FuturePaymentsFilterModalProps = {
    onSubmit: (accounts: Account[]) => void;
    accounts: Account[];
    open: boolean;
    setOpen: React.Dispatch<boolean>;
};

const FuturePaymentsFilterModal: React.FC<FuturePaymentsFilterModalProps> = ({ onSubmit, accounts, setOpen, open }) => {
    const translated = useTranslation();
    const [filterAccounts, setFilterAccounts] = useState(accounts);
    useEffect(() => {
        setFilterAccounts(accounts);
    }, [accounts, open]);
    const toggleAccountInList = useCallback(
        (account: Account) => {
            if (filterAccounts.includes(account)) {
                setFilterAccounts((prev) => prev.filter((x) => x.accountId.value !== account.accountId.value));
            } else {
                setFilterAccounts((prev) => [...prev, account]);
            }
        },
        [filterAccounts, setFilterAccounts],
    );

    const submitForm = useCallback(
        (e?: React.FormEvent<HTMLFormElement>) => {
            e?.preventDefault();
            onSubmit(filterAccounts);
        },
        [filterAccounts, onSubmit],
    );

    const viewType = useViewType();

    return (
        <EikaModal
            id="future-payments-filter-modal"
            open={open}
            setOpen={setOpen}
            size="wide"
            footerContent={
                viewType === ViewType.desktop ? (
                    <div className="future-payments-filter-modal__footer-content">
                        <Button onClick={() => submitForm()}>{translated.futurePaymentsList.choose}</Button>
                    </div>
                ) : undefined
            }
            title={translated.futurePaymentsList.chooseAccount}
        >
            <form onSubmit={submitForm}>
                <LinkButton onClick={() => setFilterAccounts(filterAccounts.length > 0 ? [] : accounts)}>
                    {filterAccounts.length > 0
                        ? translated.futurePaymentsList.removeAllSelected
                        : translated.futurePaymentsList.selectAll}
                </LinkButton>
                <CheckboxPanels
                    label=""
                    options={
                        accounts?.map((account) => {
                            return {
                                key: account.accountId.value,
                                onValueChange: () => toggleAccountInList(account),
                                checked: filterAccounts.includes(account),
                                label: account.alias,
                                description: formatAccountNumber(account.accountNumber),
                                id: account.accountId.value,
                                keygen: account.accountId.value,
                                value: account.accountId.value,
                            };
                        }) ?? []
                    }
                />
                {viewType !== ViewType.desktop && (
                    <StickyFooterRow className="future-payments-filter-modal__footer-content">
                        <Button type="submit">{translated.futurePaymentsList.choose}</Button>
                    </StickyFooterRow>
                )}
            </form>
        </EikaModal>
    );
};

export default FuturePaymentsFilterModal;
